import { type SupportChatProvider, beaconSupportChatEvent } from "@/api/supportChat"
import { Feature, isFeatureEnabled } from "@/util/gating"
import {
  hideIntercom,
  isIntercomLoaded,
  showIntercom,
  showNewIntercomMessage,
  subscribeIntercomUnreadCount,
  subscribeIntercomVisibility,
} from "@/util/intercom"
import {
  hidePylon,
  isPylonLoaded,
  showNewPylonMessage,
  showPylon,
  subscribePylonUnreadCount,
  subscribePylonVisibility,
} from "@/util/pylon"
import { logEvent } from "@/util/telemetry"

export interface SupportChatVisibilityHandlers {
  onShow: () => void
  onHide: () => void
}

// Whether Pylon (rather than Intercom) is the active support provider. Mirrors
// the server-side render decision in index.erb: Pylon renders when either the
// JWT gate or the legacy HMAC gate is on. `PylonChatJwt` swaps only the
// identity-verification scheme (signed token vs HMAC email hash); from the
// client's perspective the provider is still Pylon, so both gates select it.
export function isPylonSupportChatEnabled(): boolean {
  return isFeatureEnabled(Feature.PylonChat) || isFeatureEnabled(Feature.PylonChatJwt)
}

// The provider selected for this user. Whether the widget renders at all is
// decided server-side; this only mirrors that choice so the client can confirm
// the right widget actually loaded.
export function getExpectedSupportChatProvider(): SupportChatProvider {
  return isPylonSupportChatEnabled() ? "pylon" : "intercom"
}

function isSupportChatProviderLoaded(provider: SupportChatProvider): boolean {
  return provider === "pylon" ? isPylonLoaded() : isIntercomLoaded()
}

// A logged-in dashboard user should always end up with a working chat widget,
// but the widget scripts load asynchronously and can be blocked (CSP, ad
// blockers, missing config). Poll for the expected provider so we either
// confirm it resolved or, after a grace period, record a failure — guaranteeing
// every session reports exactly one outcome instead of failing silently.
const RESOLUTION_TIMEOUT_MS = 15_000
const RESOLUTION_POLL_INTERVAL_MS = 500

function captureSupportChatResolutionFailure(
  provider: SupportChatProvider,
  elapsedMs: number,
): void {
  logEvent("error", `Support chat failed to resolve (${provider})`, {
    sentryContext: "SupportChat",
    fingerprint: ["support-chat-unresolved", provider],
    extra: {
      expectedProvider: provider,
      pylonChatGateEnabled: isFeatureEnabled(Feature.PylonChat),
      pylonChatJwtGateEnabled: isFeatureEnabled(Feature.PylonChatJwt),
      pylonLoaded: isPylonLoaded(),
      intercomLoaded: isIntercomLoaded(),
      elapsedMs,
    },
  })
}

// Begins watching for the expected widget to load and reports the single
// resolved/failed outcome. Safe to call once per authenticated session. Must
// only run in dashboard sessions where a widget is expected — embed iframes
// (ImporterLauncherPage) have no chat widget and would always report failure.
export function resolveSupportChat(): void {
  if (process.env.NODE_ENV === "development") {
    return
  }

  const provider = getExpectedSupportChatProvider()
  const startedAt = Date.now()

  const poll = (): void => {
    // Check load before the deadline: a widget that boots in the final polling
    // interval is working and must report "resolved", not a false "failed".
    // Polling stops once the deadline passes, so this can't report "resolved"
    // for a widget that loads well after the budget.
    if (isSupportChatProviderLoaded(provider)) {
      void beaconSupportChatEvent(provider, "resolved")
      return
    }

    const elapsedMs = Date.now() - startedAt
    if (elapsedMs >= RESOLUTION_TIMEOUT_MS) {
      captureSupportChatResolutionFailure(provider, elapsedMs)
      void beaconSupportChatEvent(provider, "failed")
      return
    }

    window.setTimeout(poll, RESOLUTION_POLL_INTERVAL_MS)
  }

  poll()
}

// Records that the user actually opened the chat composer, so usage can be
// distinguished from a widget that merely loaded.
export function reportSupportChatOpened(): void {
  void beaconSupportChatEvent(getExpectedSupportChatProvider(), "opened")
}

// Pylon is the selected provider and a user invoked a support CTA, but
// `window.Pylon` is absent so the click silently does nothing. In the
// Pylon-only steady state a logged-in user must always have a working widget,
// so this means the widget never loaded — typically a missing PYLON_APP_ID /
// identity setup in the environment, or a blocked widget script. Capture it
// instead of leaving the button dead. The fingerprint groups every occurrence
// into one Sentry issue
// rather than paging per click.
function captureWidgetUnavailable(): void {
  logEvent(
    "error",
    "PylonChat enabled but Pylon widget unavailable on support CTA click",
    {
      sentryContext: "Pylon",
      fingerprint: ["pylon-widget-unavailable"],
    },
  )
}

// Routes in-app support CTAs to Pylon or Intercom based on the active provider.
// Returns whether a chat widget handled the request, preserving the boolean
// contract callers rely on to fall back (e.g. to a pricing link).
export function showNewSupportMessage(message?: string): boolean {
  if (isPylonSupportChatEnabled()) {
    const handled = showNewPylonMessage(message)
    if (!handled) {
      captureWidgetUnavailable()
    }
    return handled
  }
  return showNewIntercomMessage(message)
}

// Opens the support chat window without prepopulating a message.
export function showSupportChat(): boolean {
  if (isPylonSupportChatEnabled()) {
    const handled = showPylon()
    if (!handled) {
      captureWidgetUnavailable()
    }
    return handled
  }
  return showIntercom()
}

// Closes the support chat window.
export function hideSupportChat(): boolean {
  if (isPylonSupportChatEnabled()) {
    return hidePylon()
  }
  return hideIntercom()
}

// Subscribes to open/close events from the active provider so callers can
// reflect widget state. Returns a cleanup that unregisters the handlers.
export function subscribeSupportChatVisibility(
  handlers: SupportChatVisibilityHandlers,
): () => void {
  if (isPylonSupportChatEnabled()) {
    return subscribePylonVisibility(handlers)
  }
  return subscribeIntercomVisibility(handlers)
}

// Subscribes to unread-count changes from the active provider so callers can
// surface an unread badge. Returns a cleanup that unregisters the handler.
export function subscribeSupportChatUnreadCount(
  handler: (count: number) => void,
): () => void {
  if (isPylonSupportChatEnabled()) {
    return subscribePylonUnreadCount(handler)
  }
  return subscribeIntercomUnreadCount(handler)
}
